import { NestableEntity } from 'typed/api/entities';
import { Action, } from 'typed/actions';
import { IReducer } from './';
import { IEntitySet, isNetworkArray } from './entityReducers';
import { deepAssign, DeepPartial } from 'typed/utils';

export default function nestableEntitiesReducer<R>(reducer: R): R;
export default function nestableEntitiesReducer<E extends NestableEntity>(reducer: IReducer<IEntitySet<E>, Action>): IReducer<IEntitySet<E>, Action> {
    return function nestableEntitiesReducer(state, action) {
        const
            oldState = state,
            newState = reducer(state, action);

        if (!oldState || newState === oldState) {
            return newState;
        }

        let
            oldData = isNetworkArray(oldState) ? oldState.Data : oldState,
            newData = isNetworkArray(newState) ? newState.Data : newState;

        if (!oldData.length || !newData.length) {
            return isNetworkArray(newState) ? deepAssign(newState, { Data: newData }) : newData;
        }

        const changes = oldData.reduce((r, oldEntity) => [
            ...r,
            ...newData
                .filter(newEntity =>
                    (oldEntity.ID === newEntity.ID || oldEntity.Custom.internalID === newEntity.ID) &&
                    (oldEntity.FatherID !== newEntity.FatherID || oldEntity.Sort !== newEntity.Sort)
                )
                .map(newEntity => ({ oldEntity, newEntity }))
        ], [] as { oldEntity: E; newEntity: E; }[]);

        if (!changes.length) {
            return isNetworkArray(newState) ? deepAssign(newState, { Data: newData }) : newData;
        }

        if (changes.length > 1) {
            throw new Error('Can not recalculate Sort/ScopeEndSort, more than one entity have been changed.');
        }

        const { oldEntity, newEntity } = changes[0];

        if (!oldEntity && newEntity) {
            const
                newParentScope = newEntity.FatherID ?
                    oldData.getByID(newEntity.FatherID)! :
                    { DepthLevel: 0, Sort: 0, ScopeEndSort: oldData.length } as E,
                count = 1;

            newData = deepAssign(newData, newData
                .map(entity => deepAssign<NestableEntity>(entity,
                    entity.ID === newEntity.ID ? { Sort: newParentScope.ScopeEndSort + 1, ScopeEndSort: newParentScope.ScopeEndSort + count, DepthLevel: newParentScope.DepthLevel + 1 } :
                        entity.Sort <= newParentScope.Sort && entity.ScopeEndSort >= newParentScope.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort + count } :
                            entity.Sort > newParentScope.ScopeEndSort ? { Sort: entity.Sort + count, ScopeEndSort: entity.ScopeEndSort + count } :
                                entity) as E)
                .sortBy(({ Sort }) => Sort)
            );
        }
        else if (oldEntity && newEntity) {
            const
                newParentScope = newEntity.FatherID ?
                    oldData.getByID(newEntity.FatherID)! :
                    { DepthLevel: 0, Sort: 0, ScopeEndSort: oldData.length } as E,
                count = oldEntity.ScopeEndSort - oldEntity.Sort + 1,
                insertGapOffset = newParentScope.ScopeEndSort < oldEntity.Sort ? count : 0,
                scopeAfterInsertGap = { Sort: oldEntity.Sort + insertGapOffset, ScopeEndSort: oldEntity.ScopeEndSort + insertGapOffset },
                offset = newParentScope.ScopeEndSort + 1 - scopeAfterInsertGap.Sort;

            newData = deepAssign(newData, newData
                .map(entity => deepAssign<NestableEntity>(entity,
                    entity.Sort <= newParentScope.Sort && entity.ScopeEndSort >= newParentScope.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort + count } :
                        entity.Sort > newParentScope.ScopeEndSort ? { Sort: entity.Sort + count, ScopeEndSort: entity.ScopeEndSort + count } :
                            entity) as E)
                .map(entity => deepAssign<NestableEntity>(entity,
                    entity.Sort >= scopeAfterInsertGap.Sort && entity.ScopeEndSort <= scopeAfterInsertGap.ScopeEndSort ?
                        { Sort: entity.Sort + offset, ScopeEndSort: entity.ScopeEndSort + offset, DepthLevel: newParentScope.DepthLevel + 1 } :
                        entity) as E)
                .map(entity => deepAssign<NestableEntity>(entity,
                    entity.Sort <= scopeAfterInsertGap.Sort && entity.ScopeEndSort >= scopeAfterInsertGap.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort - count } :
                        entity.Sort > scopeAfterInsertGap.ScopeEndSort ? { Sort: entity.Sort - count, ScopeEndSort: entity.ScopeEndSort - count } :
                            entity) as E)
                .sortBy(({ Sort }) => Sort)
            );
        }
        else if (oldEntity && !newEntity) {
            const count = oldEntity.ScopeEndSort - oldEntity.Sort + 1;

            newData = deepAssign(newData, newData
                .filter(entity => entity.Sort < oldEntity.Sort || entity.ScopeEndSort > oldEntity.ScopeEndSort)
                .map(entity => deepAssign(entity, (
                    entity.Sort <= oldEntity.Sort && entity.ScopeEndSort >= oldEntity.ScopeEndSort ? { ScopeEndSort: entity.ScopeEndSort - count } :
                        entity.Sort > oldEntity.ScopeEndSort ? { Sort: entity.Sort - count, ScopeEndSort: entity.ScopeEndSort - count } :
                            entity
                ) as DeepPartial<E>))
            );
        }
        else {
            newData = newData;
        }

        return isNetworkArray(newState) ? deepAssign(newState, { Data: newData }) : newData;
    }
}